import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

/**
 * OKS-466 — Verified TikTok identity header for the combined signup
 * screen + the OAuth success interstitial (UX Spec §C / §B).
 *
 * Renders the avatar (with a monogram fallback when missing), the
 * display name, the `@handle`, and the follower count. The follower line
 * is a FIRM requirement (sourced from the validated `user.info.stats`
 * scope) and is always shown on the combined screen — the only path that
 * omits it is the denied-scope OAuth state, which never reaches this
 * component.
 *
 * # Follower formatting
 *
 * Compact locale-aware via `Intl.NumberFormat` (`1.3K`, `1.2M`). Decorative
 * — the count is never the sole carrier of meaning (UX Spec §6 a11y), so
 * it sits alongside the always-present name + handle.
 */
export interface TiktokIdentityHeaderProps {
  displayName: string | null
  handle: string | null
  avatarUrl: string | null
  followerCount: number
  testId?: string
}

function monogram(name: string | null, handle: string | null): string {
  const source = (name ?? handle ?? "?").trim()
  return source.slice(0, 1).toUpperCase() || "?"
}

export function TiktokIdentityHeader({
  displayName,
  handle,
  avatarUrl,
  followerCount,
  testId = "signup-identity-header",
}: TiktokIdentityHeaderProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || "fr"

  const followers = new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Math.max(0, followerCount))

  let name = displayName?.trim() ?? ""
  if (!name && handle) name = `@${handle}`

  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 rounded-lg border p-3"
    >
      <Avatar className="size-12">
        {avatarUrl && (
          <AvatarImage
            src={avatarUrl}
            alt={t("signups.details.identity.avatar_alt", { name })}
          />
        )}
        <AvatarFallback data-testid={`${testId}-monogram`}>
          {monogram(displayName, handle)}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col">
        <span
          className="text-foreground truncate text-sm font-medium"
          data-testid={`${testId}-name`}
        >
          {name}
          {handle && displayName && (
            <span className="text-muted-foreground font-normal">
              {" "}
              · @{handle}
            </span>
          )}
        </span>
        <span
          className="text-muted-foreground text-sm"
          data-testid={`${testId}-followers`}
        >
          {followers} {t("signups.details.identity.followers_label")}
        </span>
      </div>
    </div>
  )
}

export default TiktokIdentityHeader
